import { getNaturalAspect } from "../imageStore/imageStore";
import {
  homogeneous2descartes,
  truncatedSVDLeastSquares as solve,
} from "../util/basicAlgebra";
import { mapValues, range } from "lodash";
import { clipToBounds } from "../util/general";
import Matrix from "ml-matrix";

const getGenericCircleXY = (jValues, N, radius = 1) => {
  const thetas = jValues.map((value) => (2 * Math.PI * value) / N);
  const x = thetas.map((theta) => radius * Math.cos(theta));
  const y = thetas.map((theta) => radius * Math.sin(theta));
  return { x, y };
};
export function circleHomography(uvObject, N) {
  const jValues = Object.keys(uvObject);

  // get xy coordinates (just points on a circle)
  const { x, y } = getGenericCircleXY(jValues, N);

  // create the design matrix and the free term
  let A = [];
  let BVector = [];
  const unconstrainedThreshold = window.SirvHSE?.unconstrainedThreshold || 4;
  let out = null;
  if (
    unconstrainedThreshold &&
    jValues.length >= unconstrainedThreshold &&
    unconstrainedThreshold >= 4
  ) {
    //unconstrained, need 4 points
    for (let i = 0; i < x.length; i++) {
      let j = jValues[i];
      let u = uvObject[j][0];
      let v = uvObject[j][1];
      A.push([x[i], y[i], 1, 0, 0, 0, -u * x[i], -u * y[i]]);
      A.push([0, 0, 0, x[i], y[i], 1, -v * x[i], -v * y[i]]);
      BVector.push(u);
      BVector.push(v);
    }
    try {
      // solve using least squares // inv(A.T @ A) @ A.T @ b
      const [a, b, c, d, e, f, g, h] = solve({ A, b: BVector });
      out = [
        [a, b, c],
        [d, e, f],
        [g, h, 1],
      ];
    } catch (error) {
      console.log(error);
      return null;
    }
  } else {
    for (let i = 0; i < x.length; i++) {
      A.push([x[i], y[i], 1, 0, 0, 0]);
      A.push([0, 0, 0, x[i], y[i], 1]);
      let j = jValues[i];
      BVector.push(uvObject[j][0]);
      BVector.push(uvObject[j][1]);
    }
    // solve using least squares // inv(A.T @ A) @ A.T @ b
    const [a, b, c, d, e, f] = solve({ A, b: BVector });
    out = [
      [a, b, c],
      [d, e, f],
      [0, 0, 1],
    ];
  }
  return out;
}

export const getCircleProjectionVectorized = ({
  imageXYPercentObject,
  frameCount,
}) => {
  // here j index refers to frame index
  const aspect = getNaturalAspect();
  // uv scaled to unity width
  const u0 = 0.5;
  const v0 = 0.5 * aspect;
  const uvObject = mapValues(imageXYPercentObject, ({ x, y }) => [
    x / 100 - u0,
    y / 100 - v0,
  ]);
  const H = circleHomography(uvObject, frameCount);
  if (!H) {
    return;
  }
  const outputJVector = range(frameCount);
  const { x, y } = getGenericCircleXY(outputJVector, frameCount);
  const xHomogeneousVector = [x, y, x.map((xi) => 1)];
  // const uHomogeneousVector = multiply(H, xHomogeneousVector);
  const uHomogeneousVector = new Matrix(H)
    .mmul(new Matrix(xHomogeneousVector))
    .to2DArray();
  const [uArray, vArray] = homogeneous2descartes(uHomogeneousVector);
  const outputXArray = uArray.map((value) => 100 * (value + u0));
  const outputYArray = vArray.map((value) => 100 * (value + v0));
  let output = {};
  let clipping = [];
  for (let j of outputJVector) {
    if (
      outputXArray[j] > 100 ||
      outputXArray[j] < 0 ||
      outputYArray[j] > 100 ||
      outputYArray[j] < 0
    ) {
      clipping.push(j);
    }
    output[j] = {
      frame: j,
      x: clipToBounds(outputXArray[j], 0, 100),
      y: clipToBounds(outputYArray[j], 0, 100),
    };
  }
  return { output, clipping };
};
